import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import SectionWrapper from '../section-wrapper/SectionWrapper';
import { RESONANCE_SORT, SORT_ORDER } from '../../../../../constants';
import formatLinksToStackedHorizontalBarChartData from '../../../../../utils/formatLinksToStackedHorizontalBarChartData';
import StackedHorizontalBars from '../../../../../components/charts/chartjs/stacked-horizontal-bars/StackedHorizontalBars';
import { getChartValueColor } from '../../../../../utils/chart';
import createTooltipMarkup from '../high-performing-variables/tooltip-markup/tooltipMarkup';
import HighPerformingMessagesFilters from './filters/HighPerformingMessagesFilters';
import HighPerformingSubtitle from '../high-performing-subtitle/HighPerformingSubtitle';

function HighPerformingMessages({ selectedMessage }) {
  const { t } = useTranslation('resonance');

  const [isGlobalPercentage, setIsGlobalPercentage] = useState(false);
  const [sort, setSort] = useState({ sort: RESONANCE_SORT.TOTAL, direction: SORT_ORDER.DESC });
  const [size, setSize] = useState(5);
  const [search, setSearch] = useState('');
  const theme = useSelector((state) => state.global.theme);

  const relatedMessagesLinks = useSelector((state) => state.resonance.correlatedMessages);

  const mappedValueSelectors = {
    [RESONANCE_SORT.TOTAL]: (link) => (isGlobalPercentage ? link.globalTotal : link.total),
    [RESONANCE_SORT.R1]: (link) => (isGlobalPercentage ? link.globalR1 : link.r1),
    [RESONANCE_SORT.R2]: (link) => (isGlobalPercentage ? link.globalR2 : link.r2),
  };

  const selectedValueSelector = mappedValueSelectors[sort.sort];

  const searchedLinks = relatedMessagesLinks.filter(({ targetMessage }) =>
    targetMessage.toLowerCase().includes(search.toLowerCase()),
  );

  const sortedLinks = [...searchedLinks].sort((a, b) =>
    sortWithDirection(sort.direction, selectedValueSelector(a), selectedValueSelector(b)),
  );
  const slicedLinks = sortedLinks.slice(0, size);

  const chartsData = formatLinksToStackedHorizontalBarChartData(
    slicedLinks,
    theme,
    isGlobalPercentage,
    ({ targetMessage }) => targetMessage,
  );

  const chartConfig = {
    tooltip: {
      createMarkup: (tooltipModel, data) => createTooltipMarkup({ tooltipModel, data, isGlobal: isGlobalPercentage }),
    },
    datalabels: {
      anchor: 'center',
      align: 'center',
      color: getChartValueColor(theme),
      font: {
        size: '12px',
        weight: 'bold',
        family: 'Inter',
      },
      formatter: function (value) {
        return value ? value + '%' : '';
      },
    },
  };

  return (
    <SectionWrapper
      title={t('performingMessages', { direction: t(sort.direction === SORT_ORDER.DESC ? 'high' : 'low') })}
      Subtitle={() => <HighPerformingSubtitle selectedMessage={selectedMessage} />}
      Filters={HighPerformingMessagesFilters({
        isGlobalPercentage: isGlobalPercentage,
        sort: sort,
        size: size,
        onChangeSize: setSize,
        onChangeGlobalPercentage: setIsGlobalPercentage,
        onChangeSort: setSort,
        search: search,
        onChangeSearch: setSearch,
      })}>
      <StackedHorizontalBars
        data={chartsData}
        config={chartConfig}
        labels={slicedLinks.map(({ targetMessage }) => targetMessage)}
      />
    </SectionWrapper>
  );
}

export default HighPerformingMessages;

const sortWithDirection = (direction, a, b) => {
  if (direction === SORT_ORDER.DESC) {
    return b - a;
  }

  return a - b;
};
